import React, { useState, useEffect } from 'react';

const RefundProtectionInfo = ({ quotes = [], onSelectionChange, showError = false, isDirectorView = false }) => {
    const [showDetails, setShowDetails] = useState(false);
    const [selections, setSelections] = useState({});

    // Parent checkout keeps the original off-card badge placement;
    // director/super director booking flow needs it inline above the card instead.
    const recommendedBadgeStyle = isDirectorView
        ? {
            position: 'absolute', top: -10, right: 5,
            transform: 'translateY(0)',
            backgroundColor: '#842014', color: '#fff',
            padding: '5px 48px', borderRadius: '6px',
            fontSize: '0.85rem', fontWeight: '700', letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
        }
        : {
            position: 'absolute', top: 13, right: -100,
            transform: 'translateY(-75%)',
            backgroundColor: '#842014', color: '#fff',
            padding: '5px 48px', borderRadius: '6px',
            fontSize: '0.85rem', fontWeight: '700', letterSpacing: '0.5px',
            whiteSpace: 'nowrap',
        };

    useEffect(() => {
        const synced = {};
        quotes.forEach((q, idx) => {
            // Only auto-sync accepted state; never pre-select Decline
            if (q.selectedState === 'ACCEPTED') synced[idx] = true;
        });
        if (Object.keys(synced).length > 0) {
            setSelections(prev => ({ ...prev, ...synced }));
        }
    }, [quotes]);

    const handleSelect = (idx, isAccept) => {
        setSelections(prev => ({ ...prev, [idx]: isAccept }));
        if (onSelectionChange) onSelectionChange(idx, isAccept);
    };

    return (
        <div className="refund-protection-info mb-4 position-relative rounded p-4" style={{ marginTop: '40px', border: '1.5px solid #b0b0b0' }}>
            <span style={recommendedBadgeStyle}>
                Recommended
            </span>

            <h5 className="mb-3">Add Refund Protection</h5>

            <p className="mb-2">
                <strong>REFUND POLICY:</strong>{' '}
                <span className="color_red">All tuition is non-refundable unless cancellation protection is purchased.</span>
            </p>

            <p className="mb-3">
                <strong>CANCELLATION PROTECTION:</strong> Protect your registration and receive up to a 100% reimbursement if the player is unable to participate{' '}
                <strong><u>for a covered reason</u></strong>. Coverage is provided by Vertical Insure (VI-Coverage).
            </p>

            <div className="row mb-2">
                <div className="col-md-6">
                    <p className="mb-1"><strong>What's Covered:</strong></p>
                    <ul className="list-unstyled">
                        <li className="mb-1"><span className="text-success me-2">✓</span>Season-Ending Injury (on or off the field)</li>
                        <li className="mb-1"><span className="text-success me-2">✓</span>Season-Ending Illness</li>
                        <li className="mb-1"><span className="text-success me-2">✓</span>Short-Term Injuries &amp; Illnesses</li>
                        <li className="mb-1"><span className="text-success me-2">✓</span>Job Loss (parent unexpectedly loses employment)</li>
                        <li className="mb-1"><span className="text-success me-2">✓</span>Active Military Duty (parent called to duty or reassigned)</li>
                        <li className="mb-1"><span className="text-success me-2">✓</span>Severe Weather / Hazardous Travel Conditions</li>
                        <li className="mb-1"><span className="text-success me-2">✓</span>Traffic Accidents preventing attendance</li>
                    </ul>
                </div>
                <div className="col-md-6">
                    <p className="mb-1"><strong>What's Not Covered:</strong></p>
                    <ul className="list-unstyled">
                        <li className="mb-1"><span className="text-danger me-2">✗</span>Athlete decides not to participate</li>
                        <li className="mb-1"><span className="text-danger me-2">✗</span>Personal schedule conflicts</li>
                        <li className="mb-1"><span className="text-danger me-2">✗</span>Pre-existing medical conditions</li>
                    </ul>
                </div>
            </div>

            <p className="mb-3">
                <em><small>Please refer to your policy for a full list of covered perils. Coverage may vary by state.</small></em>
            </p>

            <div className="coverage-details-accordion border rounded mb-3">
                <div
                    className="d-flex justify-content-between align-items-center p-3"
                    onClick={() => setShowDetails(!showDetails)}
                    style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', borderRadius: showDetails ? '4px 4px 0 0' : '4px' }}
                >
                    <span>Coverage Details &amp; Policy Information</span>
                    <span>{showDetails ? '▲' : '▼'}</span>
                </div>
                {showDetails && (
                    <div className="p-3 border-top">
                        <p className="mb-2">
                            <strong>Learn More About Your Coverage:</strong> For an overview of what Sports Registration Protection includes, visit{' '}
                            <a href="https://vicoverage.com/sports-registration-protection" target="_blank" rel="noopener noreferrer">
                                vicoverage.com/sports-registration-protection
                            </a>.
                        </p>
                        <p className="mb-2">
                            <strong>View Your Coverage &amp; File a Claim:</strong> Visit the{' '}
                            <a href="https://vicoverage.com/customer-portal/" target="_blank" rel="noopener noreferrer">VI-Coverage Customer Portal</a>
                            {' '}to view your active policy and begin the claims process. You can create a free account using the email address provided at registration.
                        </p>
                        <p className="mb-2">Cancellation claims are handled directly by the insurance provider.</p>
                        <p className="mb-2">
                            <strong>Questions?</strong> Reach out to our support team at{' '}
                            <a href="mailto:support@vicoverage.com">support@vicoverage.com</a>.
                        </p>
                        <p className="mb-2"><strong>DEDUCTIONS:</strong> If you arrive late or leave early, there is no tuition deduction.</p>
                        <p className="mb-0">Cancellation protection does not cover campers living outside of the United States. For international campers, please call us at 301-575-6480.</p>
                    </div>
                )}
            </div>

            {quotes.length > 0 ? (
                <>
                    {quotes.map((quote, idx) => (
                        <div key={quote.quoteId || idx} className="mb-2">
                            <div
                                className="border rounded p-3 mb-1 d-flex align-items-center justify-content-between"
                                style={{ cursor: 'pointer', backgroundColor: selections[idx] === true ? '#f0f8ff' : '' }}
                                onClick={() => handleSelect(idx, true)}
                            >
                                <label className="d-flex align-items-center mb-0" style={{ cursor: 'pointer', flex: 1 }}>
                                    <input
                                        type="radio"
                                        name={`refund-protection-${idx}`}
                                        checked={selections[idx] === true}
                                        onChange={() => handleSelect(idx, true)}
                                        onClick={e => e.stopPropagation()}
                                        className="me-2"
                                    />
                                    <span>
                                        Include Coverage for {quote.name}
                                        {quote.description ? ` (${quote.description})` : ''}
                                    </span>
                                </label>
                                <strong className="ms-3">${(quote.price ?? 0).toFixed(2)}</strong>
                            </div>
                            <div
                                className="border rounded p-3 d-flex align-items-center"
                                style={{ cursor: 'pointer', backgroundColor: selections[idx] === false ? '#f0f8ff' : '' }}
                                onClick={() => handleSelect(idx, false)}
                            >
                                <label className="d-flex align-items-center mb-0" style={{ cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name={`refund-protection-${idx}`}
                                        checked={selections[idx] === false}
                                        onChange={() => handleSelect(idx, false)}
                                        onClick={e => e.stopPropagation()}
                                        className="me-2"
                                    />
                                    <span>Decline Coverage</span>
                                </label>
                            </div>
                            {showError && selections[idx] === undefined && (
                                <p className="mb-0 mt-1" style={{ color: '#842014', fontWeight: '600' }}>A selection is required.</p>
                            )}
                        </div>
                    ))}
                    <p className="mt-2">
                        <small>
                            Accepting coverage constitutes my electronic signature. I confirm that I have read, understand, and agree to the{' '}
                            <a href="https://www.verticalinsure.com/insurance-notices" target="_blank" rel="noopener noreferrer">
                                insurance notices and disclosures
                            </a>.
                        </small>
                    </p>
                </>
            ) : (
                <div className="text-center py-2">
                    <small className="text-muted">Loading coverage options...</small>
                </div>
            )}
        </div>
    );
};

export default RefundProtectionInfo;
